import type { ShotFrameCaptureRow } from "@/lib/shot-frame-captures-api"
import { getCsrfToken, type ShotFrameSource, type ShotMediaVersionFrameRow } from "@/lib/workspace-api"
import { handleJson } from "@/lib/json-request"
import type { SceneLayoutState, SceneStateSavePayload } from "@/lib/location-scene-3d/scene-state"

// --- Дерево библиотеки (Inertia props страницы workspace/Library) ---

export type LibraryTreeShot = {
  id: number
  number: string
  position?: number
  shot_kind: string
  /** Кадр шота для плитки в дереве: опорник, иначе первый кадр видео. */
  poster_url: string | null
  /** Сколько ассетов лежит в папке шота (кадры, версии, скриншоты, референсы). */
  asset_count: number
}

export type LibraryTreeScene = {
  id: number
  number: number
  title: string | null
  shots: LibraryTreeShot[]
}

export type LibraryTreeEpisode = {
  id: number
  number: number
  title: string | null
  /** Сколько сегментов в ленте аниматика эпизода: 0 — узла «Аниматик» в дереве нет. */
  animatic_segment_count: number
  scenes: LibraryTreeScene[]
}

export type LibraryTreeSeason = {
  id: number
  number: number
  episodes: LibraryTreeEpisode[]
}

export type LibraryTree = { seasons: LibraryTreeSeason[] }

export type LibraryCanvasRow = {
  id: number
  number: number
  title: string
  generation_count: number
}

export type LibraryFlowBoardRow = {
  id: number
  number: number
  title: string
  asset_count: number
}

/** Холст флоу, у которого доски уже нет: те же файлы, другая папка. */
export type LibraryFlowOrphanRow = {
  orphan: true
  title: string
  asset_count: number
}

export type LibraryFlowRow = LibraryFlowBoardRow | LibraryFlowOrphanRow

export function isLibraryFlowOrphan(row: LibraryFlowRow): row is LibraryFlowOrphanRow {
  return "orphan" in row && row.orphan === true
}

export function libraryFlowBoards(rows: LibraryFlowRow[]): LibraryFlowBoardRow[] {
  return rows.filter((row): row is LibraryFlowBoardRow => !isLibraryFlowOrphan(row))
}

export const EMPTY_LIBRARY_FLOWS: LibraryFlowRow[] = []

export type LibraryFlowItem = {
  key: string
  kind: "image" | "video"
  label: string
  created_at: string | null
  thumb_url: string | null
  url: string | null
  download_url: string | null
  duration_ms: number | null
  source: ShotFrameSource
}

export type LibraryFlowGroup = {
  key: string
  label: string
  items: LibraryFlowItem[]
}

/** Счётчик секции «Эскизы» в дереве Библиотеки. */
export type LibrarySketchesSummary = {
  count: number
}

export const EMPTY_LIBRARY_SKETCHES: LibrarySketchesSummary = { count: 0 }

/** Счётчик секции «Загрузки» в дереве Библиотеки. */
export type LibraryUploadsSummary = {
  count: number
}

export const EMPTY_LIBRARY_UPLOADS: LibraryUploadsSummary = { count: 0 }

/** Строка сущности в дереве: имя, обложка и счётчик ассетов её папки. */
export type LibraryEntityRow = {
  id: number
  name: string | null
  poster_url: string | null
  asset_count: number
}

export type LibraryEntitySections = {
  characters: LibraryEntityRow[]
  locations: LibraryEntityRow[]
  props: LibraryEntityRow[]
  scenes_3d: LibraryEntityRow[]
}

export const EMPTY_LIBRARY_ENTITIES: LibraryEntitySections = {
  characters: [],
  locations: [],
  props: [],
  scenes_3d: [],
}

function getHeaders(): HeadersInit {
  return {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  }
}

// --- Дерево по требованию: панель шота открывает Библиотеку там, где props страницы нет ---

export type LibraryTreePayload = {
  tree: LibraryTree
  canvases: LibraryCanvasRow[]
  flows: LibraryFlowRow[]
  entities: LibraryEntitySections
  sketches: LibrarySketchesSummary
  uploads: LibraryUploadsSummary
  active_episode_id: number | null
}

/**
 * Кэш на процесс: пикер открывается по многу раз за сессию и должен рисоваться
 * мгновенно. Явной инвалидации нет — состав папок меняют слишком многие места
 * (новая сцена, шот, доска, скриншот, удаление загрузки), и любой пропущенный
 * вызов оставил бы дерево устаревшим до перезагрузки страницы. Вместо этого
 * потребитель читает кэш для первой отрисовки и тут же запрашивает свежее
 * дерево (stale-while-revalidate) — см. readCachedLibraryTree.
 */
const treeCache = new Map<number, LibraryTreePayload>()

export function readCachedLibraryTree(projectId: number): LibraryTreePayload | null {
  return treeCache.get(projectId) ?? null
}

export async function fetchLibraryTree(
  projectId: number,
  options?: { signal?: AbortSignal; force?: boolean },
): Promise<LibraryTreePayload> {
  if (!options?.force) {
    const cached = treeCache.get(projectId)
    if (cached) return cached
  }
  const res = await fetch(`/projects/${projectId}/workspace/library/tree`, {
    credentials: "same-origin",
    signal: options?.signal,
    headers: getHeaders(),
  })
  const data = await handleJson<LibraryTreePayload>(res)
  const payload: LibraryTreePayload = {
    tree: data.tree ?? { seasons: [] },
    canvases: data.canvases ?? [],
    flows: data.flows ?? EMPTY_LIBRARY_FLOWS,
    entities: {
      characters: data.entities?.characters ?? [],
      locations: data.entities?.locations ?? [],
      props: data.entities?.props ?? [],
      scenes_3d: data.entities?.scenes_3d ?? [],
    },
    sketches: data.sketches ?? EMPTY_LIBRARY_SKETCHES,
    uploads: data.uploads ?? EMPTY_LIBRARY_UPLOADS,
    active_episode_id: data.active_episode_id ?? null,
  }
  treeCache.set(projectId, payload)
  return payload
}

// --- Ассеты шота, сцены или эпизода ---

export type LibraryReferenceInputRow = {
  id: number
  label: string
  url: string | null
  thumb_url?: string | null
  download_url?: string | null
  media_type: "image" | "video" | "audio" | "voice"
  created_at?: string | null
}

export type LibraryShotFolderShot = {
  id: number
  number: number
  storyboard_preview_url: string | null
  shooting_board_preview_url: string | null
  /** false — превью есть, а файла за ним нет (legacy URL-only): назначить такой кадр нельзя. */
  storyboard_attached: boolean
  shooting_board_attached: boolean
  storyboard_created_at?: string | null
  shooting_board_created_at?: string | null
  shot_reference_inputs: Partial<Record<"storyboard" | "video" | "audio" | "voice", LibraryReferenceInputRow[]>>
  audio_assets?: LibraryShotAudioRow[]
  animatic_sources?: LibraryShotAnimaticRow[]
}

/** Аниматик, загруженный прямо в шоте, — включая откреплённые (история версий шота). */
export type LibraryShotAnimaticRow = {
  id: number
  original_filename: string | null
  status: string
  duration_ms: number | null
  frame_rate: number | null
  aspect_ratio: number | null
  created_at: string
  video_stream_url: string | null
  poster_url: string | null
  /** Прикреплён ли ролик к шоту сейчас — откреплённые рисуются историей. */
  linked: boolean
  segment_id: number | null
}

/** Звук шота: озвучка TTS, замена голоса STS, запись с микрофона, загруженный файл. */
export type LibraryShotAudioRow = {
  id: number
  kind: "upload" | "recording" | "tts"
  name: string | null
  duration_ms: number | null
  created_at: string
  url: string
  voice_name?: string | null
  /** Голос заменён через STS: отдельного kind у этого нет. */
  voice_change: boolean
}

// --- Папка сущности: герой, локация, реквизит ---

/**
 * Плитка папки сущности. Подпись фронт переводит по label_kind; label — своё имя
 * (ракурс, образ, файл), если оно есть. source отсутствует у превью без файла.
 */
export type LibraryEntityItemRow = {
  key: string
  media: "image" | "video" | "audio"
  label_kind: string
  label: string | null
  url: string | null
  thumb_url: string | null
  created_at: string | null
  source: { entity_type: string; entity_id: number; state_id?: string | null } | null
  /** Длительность видео (мс) — у превизов в папке локации. */
  duration_ms?: number | null
}

export type LibraryEntityGroupRow = {
  key: string
  /** Вид группы для перевода заголовка: outfit, variant, state, viewpoints, references и т.п. */
  kind: string
  /** Своё имя группы — образ, вариант, состояние. Для остальных null. */
  label: string | null
  items: LibraryEntityItemRow[]
  created_at: string | null
  /** Один уровень вложенности: «Референсы стен» → сцены. */
  groups?: LibraryEntityGroupRow[] | null
}

export type LibraryEntityFolder = {
  id: number
  kind: "character" | "location" | "prop" | "scene3d"
  name: string | null
  own: LibraryEntityItemRow[]
  groups: LibraryEntityGroupRow[]
}

const ENTITY_FOLDER_PATH: Record<"characters" | "locations" | "props", string> = {
  characters: "characters",
  locations: "locations",
  props: "props",
}

export async function fetchLibraryEntityFolder(
  projectId: number,
  section: "characters" | "locations" | "props" | "scenes_3d",
  entityId: number,
  options?: { signal?: AbortSignal },
): Promise<LibraryEntityFolder> {
  if (section === "scenes_3d") {
    const params = new URLSearchParams({ scene3d_id: String(entityId) })
    const res = await fetch(`/projects/${projectId}/workspace/library/scene3d_folder?${params.toString()}`, {
      credentials: "same-origin",
      signal: options?.signal,
      headers: getHeaders(),
    })
    const data = await handleJson<{ entity: LibraryEntityFolder }>(res)
    return data.entity
  }
  const path = ENTITY_FOLDER_PATH[section]
  const res = await fetch(`/projects/${projectId}/workspace/library/${path}/${entityId}`, {
    credentials: "same-origin",
    signal: options?.signal,
    headers: getHeaders(),
  })
  const data = await handleJson<{ entity: LibraryEntityFolder }>(res)
  return data.entity
}

export async function fetchLibraryFlowFolder(
  projectId: number,
  flowId: number,
  options?: { signal?: AbortSignal },
): Promise<LibraryFlowGroup[]> {
  const res = await fetch(`/projects/${projectId}/workspace/library/flows/${flowId}`, {
    credentials: "same-origin",
    signal: options?.signal,
    headers: getHeaders(),
  })
  const data = await handleJson<{ flow: { groups: LibraryFlowGroup[] } }>(res)
  return data.flow?.groups ?? []
}

export async function fetchLibraryFlowOrphans(
  projectId: number,
  options?: { signal?: AbortSignal },
): Promise<LibraryFlowGroup[]> {
  const res = await fetch(`/projects/${projectId}/workspace/library/flow_orphans`, {
    credentials: "same-origin",
    signal: options?.signal,
    headers: getHeaders(),
  })
  const data = await handleJson<{ flow: { groups: LibraryFlowGroup[] } }>(res)
  return data.flow?.groups ?? []
}

// --- Поиск по проекту ---

/** Куда ведёт результат: та же форма, что у LibrarySelection, но в терминах ответа сервера. */
export type LibrarySearchTarget =
  | { section: "shots"; shot_id?: number; scene_id?: number; episode_id?: number }
  | { section: "canvas"; canvas_id: number }
  | { section: "flow"; flow_id: number }
  | { section: "flow"; orphans: true }
  | { section: "sketches" }
  | { section: "uploads" }
  | { section: "characters" | "locations" | "props" | "scenes_3d"; entity_id: number }

/** Почему запись попала в выдачу: ключ поля переводится, фрагмент показывается как есть. */
export type LibrarySearchMatch = { field: string; snippet: string }

/** Папка, у которой совпал собственный текст: промпт шота, название сцены, имя героя. */
export type LibrarySearchPlace = {
  key: string
  target: LibrarySearchTarget
  match: LibrarySearchMatch
}

/** Файл, совпавший содержимым: генерация Холста по промпту, загрузка по имени файла. */
export type LibrarySearchAsset = {
  key: string
  media: "image" | "video" | "audio"
  label: string
  url: string | null
  thumb_url: string | null
  download_url: string | null
  created_at: string | null
  duration_ms: number | null
  /** Чем ассет является для назначения и выбора в пикере — тот же контракт, что у папок. */
  source: ShotFrameSource | null
  target: LibrarySearchTarget
  match: LibrarySearchMatch
}

export type LibrarySearchResults = {
  places: LibrarySearchPlace[]
  assets: LibrarySearchAsset[]
  /** Совпадений больше, чем влезло в потолок выдачи, — запрос стоит уточнить. */
  truncated: boolean
}

/** Короче — и запрос не имеет смысла: подстрока из одного символа найдётся почти везде. */
export const LIBRARY_SEARCH_MIN_LENGTH = 2

export async function fetchLibrarySearch(
  projectId: number,
  query: string,
  options?: { signal?: AbortSignal },
): Promise<LibrarySearchResults> {
  const params = new URLSearchParams({ q: query })
  const res = await fetch(`/projects/${projectId}/workspace/library/search?${params.toString()}`, {
    credentials: "same-origin",
    signal: options?.signal,
    headers: getHeaders(),
  })
  const data = await handleJson<LibrarySearchResults>(res)
  return {
    places: data.places ?? [],
    assets: data.assets ?? [],
    truncated: data.truncated ?? false,
  }
}

// --- Узел «Аниматик» эпизода ---

/** Сегмент ленты аниматика в Библиотеке: диапазон внутри файла-источника, не отдельный файл. */
export type LibraryAnimaticSegmentRow = {
  id: number
  position: number
  start_ms: number
  end_ms: number
  global_start_ms: number
  global_end_ms: number
  scene_number: number
  poster_url: string | null
  poster_status: string
  /** Поток файла-источника: сегмент играется как его подрезанный кусок. */
  video_stream_url: string | null
  /** Длительность файла-источника — предел перемотки для плеера. */
  source_duration_ms: number | null
  frame_rate: number | null
  aspect_ratio: number | null
  /** Шот, к которому сегмент привязан; null — свободен и его можно привязать. */
  shot: { id: number; number: string | number; scene_number: number | null } | null
}

export type LibraryEpisodeAnimatic = {
  id: number
  status: string
  duration_ms: number | null
  segments: LibraryAnimaticSegmentRow[]
}

export async function fetchLibraryEpisodeAnimatic(
  projectId: number,
  episodeId: number,
  options?: { signal?: AbortSignal },
): Promise<LibraryEpisodeAnimatic | null> {
  const params = new URLSearchParams({ episode_id: String(episodeId) })
  const res = await fetch(`/projects/${projectId}/workspace/library/animatics?${params.toString()}`, {
    credentials: "same-origin",
    signal: options?.signal,
    headers: getHeaders(),
  })
  const data = await handleJson<{ animatic: LibraryEpisodeAnimatic | null }>(res)
  return data.animatic ?? null
}

export type LibraryShotAssetsScope = { shotId: number } | { sceneId: number } | { episodeId: number }

export type LibraryShotAssetsShot = {
  id: number
  number: string | number
  shot: LibraryShotFolderShot
  versions: ShotMediaVersionFrameRow[]
  captures: ShotFrameCaptureRow[]
}

export type LibraryShotAssetsScene = {
  id: number
  number: number
  shots: LibraryShotAssetsShot[]
}

export async function fetchLibraryShotAssets(
  projectId: number,
  scope: LibraryShotAssetsScope,
  options?: { signal?: AbortSignal },
): Promise<LibraryShotAssetsScene[]> {
  const params = new URLSearchParams()
  if ("shotId" in scope) params.set("shot_id", String(scope.shotId))
  else if ("sceneId" in scope) params.set("scene_id", String(scope.sceneId))
  else params.set("episode_id", String(scope.episodeId))
  const res = await fetch(`/projects/${projectId}/workspace/library/shot_assets?${params.toString()}`, {
    credentials: "same-origin",
    signal: options?.signal,
    headers: getHeaders(),
  })
  const data = await handleJson<{ scenes: LibraryShotAssetsScene[] }>(res)
  return data.scenes ?? []
}

// --- Секция «Эскизы» ---

export type LibrarySketchItem = {
  signed_id: string
  id?: number
  url: string
  download_url?: string | null
  thumb_url?: string | null
  media_type: "image" | "video" | "audio"
  label: string
  created_at?: string | null
  duration_seconds?: number | null
}

export async function fetchLibrarySketches(
  projectId: number,
  options?: { signal?: AbortSignal },
): Promise<LibrarySketchItem[]> {
  const res = await fetch(`/projects/${projectId}/workspace/library/sketches`, {
    credentials: "same-origin",
    signal: options?.signal,
    headers: getHeaders(),
  })
  const data = await handleJson<{ ok: boolean; sketches: LibrarySketchItem[] }>(res)
  return data.sketches ?? []
}

export async function uploadProjectSketch(projectId: number, file: File): Promise<LibrarySketchItem> {
  const form = new FormData()
  form.append("file", file)
  const res = await fetch(`/projects/${projectId}/workspace/library/sketches`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: form,
  })
  const data = await handleJson<{ ok: boolean; reference: LibrarySketchItem }>(res)
  return data.reference
}

// --- Секция «Загрузки»: файлы проекта со своим именем ---

export type LibraryUploadItem = {
  /** id записи ProjectLibraryUpload: переименование и удаление. */
  id: number
  /** id блоба: именно он идёт в source назначения — запись удаляема, файл переживает её. */
  blob_id: number
  label: string
  media_type: "image" | "video" | "audio"
  url: string
  download_url: string
  /** Только у изображения: у видео и аудио превью нет, иначе в <img> уехал бы сам файл. */
  thumb_url: string | null
  created_at: string
}

/** Курсор ленты — пара (created_at, id) последней строки страницы. */
export type LibraryUploadCursor = { created_at: string; id: number }

export type LibraryUploadPage = {
  uploads: LibraryUploadItem[]
  has_more: boolean
  next_cursor: LibraryUploadCursor | null
}

export const LIBRARY_UPLOADS_PAGE_SIZE = 60

export async function fetchLibraryUploads(
  projectId: number,
  options?: { before?: LibraryUploadCursor | null; limit?: number; signal?: AbortSignal },
): Promise<LibraryUploadPage> {
  const params = new URLSearchParams()
  if (options?.before) {
    params.set("before_created_at", options.before.created_at)
    params.set("before_id", String(options.before.id))
  }
  params.set("limit", String(options?.limit ?? LIBRARY_UPLOADS_PAGE_SIZE))
  const res = await fetch(`/projects/${projectId}/workspace/library/uploads?${params.toString()}`, {
    credentials: "same-origin",
    signal: options?.signal,
    headers: getHeaders(),
  })
  const data = await handleJson<LibraryUploadPage>(res)
  return {
    uploads: data.uploads ?? [],
    has_more: data.has_more ?? false,
    next_cursor: data.next_cursor ?? null,
  }
}

function mutationHeaders(): HeadersInit {
  return { ...getHeaders(), "X-CSRF-Token": getCsrfToken() }
}

export async function uploadProjectLibraryFile(projectId: number, file: File): Promise<LibraryUploadItem> {
  const form = new FormData()
  form.append("file", file)
  const res = await fetch(`/projects/${projectId}/workspace/library/uploads`, {
    method: "POST",
    credentials: "same-origin",
    headers: mutationHeaders(),
    body: form,
  })
  const data = await handleJson<{ ok: boolean; upload: LibraryUploadItem }>(res)
  return data.upload
}

export async function renameProjectLibraryUpload(
  projectId: number,
  uploadId: number,
  name: string,
): Promise<LibraryUploadItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/uploads/${uploadId}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  })
  const data = await handleJson<{ ok: boolean; upload: LibraryUploadItem }>(res)
  return data.upload
}

export async function deleteProjectLibraryUpload(projectId: number, uploadId: number): Promise<void> {
  const res = await fetch(`/projects/${projectId}/workspace/library/uploads/${uploadId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: mutationHeaders(),
  })
  await handleJson<{ ok: boolean }>(res)
}

// --- Секция «Превизы»: видеопролёты 3D-сцен локаций ---

export type LibraryPrevizStatus = "draft" | "pending" | "processing" | "ready" | "failed"

export type LibraryPrevizItem = {
  id: number
  name: string
  status: LibraryPrevizStatus
  duration_ms: number | null
  scene3d_id?: number | null
  location_id: number | null
  location_name?: string | null
  /** Шот, к которому привязан превиз (у шота логически один превиз). */
  shot_id: number | null
  media_type: "video"
  /** Готовый mp4; пока перекодировка не закончилась — webm-исходник. */
  url: string | null
  download_url: string | null
  /** Размер файла, с которого берётся download_url (готовое видео, иначе исходник). */
  file_size?: number | null
  /** webm-исходник из MediaRecorder — fallback для просмотра до mp4. */
  source_url: string | null
  /** JPEG полосы линейки. Есть только в payload панели шота, не в списках библиотеки. */
  timeline_frame_urls?: string[]
  timeline_frame_aspect_ratio?: number | null
  /** Still с кнопки Save в 3D — обложка, пока нет video url. */
  poster_url: string | null
  depth_status?: "pending" | "processing" | "ready" | "failed" | null
  depth_url?: string | null
  depth_download_url?: string | null
  depth_color_url?: string | null
  depth_color_download_url?: string | null
  depth_error?: string | null
  /** Обе карты посчитаны с текущего исходника. */
  depth_current?: boolean
  /** Имя и обложка 3D-сцены. Только payload панели шота, не списки библиотеки. */
  scene3d_name?: string | null
  scene3d_thumb_url?: string | null
  /** Snapshot 3D-сцены на момент Save (objects / camera / path / platform). */
  scene_state?: Partial<
    Pick<
      SceneLayoutState,
      | "objects"
      | "camera_state"
      | "start_camera"
      | "camera_path"
      | "platform"
      | "cells"
      | "openings"
      | "light"
      | "faces"
    >
  > | null
  created_at: string
}

/** Detail with scene_state for editPreviz (lists omit the fat snapshot). */
export type LibraryPrevizPage = {
  previzes: LibraryPrevizItem[]
  has_more: boolean
  next_cursor: LibraryUploadCursor | null
}

export async function fetchLibraryPrevizes(
  projectId: number,
  options?: { scene3dId?: number | null; limit?: number; signal?: AbortSignal },
): Promise<LibraryPrevizPage> {
  const params = new URLSearchParams()
  if (options?.scene3dId != null) params.set("scene3d_id", String(options.scene3dId))
  if (options?.limit != null) params.set("limit", String(options.limit))
  const qs = params.toString()
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes${qs ? `?${qs}` : ""}`, {
    credentials: "same-origin",
    headers: getHeaders(),
    signal: options?.signal,
  })
  const data = await handleJson<LibraryPrevizPage>(res)
  return {
    previzes: data.previzes ?? [],
    has_more: data.has_more ?? false,
    next_cursor: data.next_cursor ?? null,
  }
}

export async function fetchPreviz(projectId: number, previzId: number): Promise<LibraryPrevizItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/${previzId}`, {
    credentials: "same-origin",
    headers: getHeaders(),
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

export async function createPreviz(
  projectId: number,
  input: {
    file: File
    name: string
    locationId?: number | null
    scene3dId?: number | null
    durationMs?: number | null
    shotId?: number | null
    sceneState?: SceneStateSavePayload
  },
): Promise<LibraryPrevizItem> {
  const form = new FormData()
  form.append("file", input.file)
  form.append("name", input.name)
  if (input.locationId != null) form.append("location_id", String(input.locationId))
  if (input.scene3dId != null) form.append("scene3d_id", String(input.scene3dId))
  if (input.durationMs != null) form.append("duration_ms", String(Math.round(input.durationMs)))
  if (input.shotId != null) form.append("shot_id", String(input.shotId))
  if (input.sceneState != null) form.append("scene_state", JSON.stringify(input.sceneState))
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes`, {
    method: "POST",
    credentials: "same-origin",
    headers: mutationHeaders(),
    body: form,
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

/** @deprecated Use createScene3d — draft layout lives on Scene3d, not Previz. */
export async function createPrevizDraft(
  projectId: number,
  input: {
    name: string
    locationId?: number | null
    sceneState: SceneStateSavePayload
  },
): Promise<LibraryScene3dItem> {
  return createScene3d(projectId, input)
}

export async function updatePrevizScene(
  projectId: number,
  previzId: number,
  sceneState: SceneStateSavePayload,
): Promise<LibraryPrevizItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/${previzId}/scene`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ scene_state: sceneState }),
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

// --- Первоклассные 3D-сцены ---

export type LibraryScene3dItem = {
  id: number
  name: string
  location_id: number | null
  location_name?: string | null
  thumb_url: string | null
  /** Ready 800px cover, else the original. Location tiles. */
  display_url?: string | null
  poster_url: string | null
  has_video?: boolean
  previz_id?: number | null
  previz_count?: number
  viewpoints?: LibraryScene3dViewpointItem[]
  scene_state?: LibraryPrevizItem["scene_state"]
  created_at: string
  updated_at?: string
}

/** Alias matching Presenters.scene3d_item. */
export type Scene3dItem = LibraryScene3dItem

export type LibraryScene3dPage = {
  scene3ds: LibraryScene3dItem[]
  has_more: boolean
  next_cursor: LibraryUploadCursor | null
}

export async function fetchLibraryScene3ds(
  projectId: number,
  options?: {
    before?: LibraryUploadCursor | null
    limit?: number
    signal?: AbortSignal
    locationId?: number | null
    unlocated?: boolean
  },
): Promise<LibraryScene3dPage> {
  const params = new URLSearchParams()
  if (options?.before) {
    params.set("before_created_at", options.before.created_at)
    params.set("before_id", String(options.before.id))
  }
  params.set("limit", String(options?.limit ?? LIBRARY_UPLOADS_PAGE_SIZE))
  if (options?.unlocated) params.set("unlocated", "1")
  else if (options?.locationId != null) params.set("location_id", String(options.locationId))
  const qs = params.toString()
  const res = await fetch(`/projects/${projectId}/workspace/library/scene3ds?${qs}`, {
    credentials: "same-origin",
    headers: getHeaders(),
    signal: options?.signal,
  })
  const data = await handleJson<LibraryScene3dPage>(res)
  return {
    scene3ds: data.scene3ds ?? [],
    has_more: data.has_more ?? false,
    next_cursor: data.next_cursor ?? null,
  }
}

export async function fetchScene3d(projectId: number, scene3dId: number): Promise<LibraryScene3dItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/scene3ds/${scene3dId}`, {
    credentials: "same-origin",
    headers: getHeaders(),
  })
  const data = await handleJson<{ ok: boolean; scene3d: LibraryScene3dItem }>(res)
  return data.scene3d
}

export async function createScene3d(
  projectId: number,
  input: {
    name: string
    locationId?: number | null
    sceneState?: SceneStateSavePayload
  },
): Promise<LibraryScene3dItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/scene3ds`, {
    method: "POST",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      name: input.name,
      location_id: input.locationId ?? undefined,
      scene_state: input.sceneState ?? {},
    }),
  })
  const data = await handleJson<{ ok: boolean; scene3d: LibraryScene3dItem }>(res)
  return data.scene3d
}

export async function updateScene3dScene(
  projectId: number,
  scene3dId: number,
  sceneState: SceneStateSavePayload,
): Promise<LibraryScene3dItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/scene3ds/${scene3dId}/scene`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ scene_state: sceneState }),
  })
  const data = await handleJson<{ ok: boolean; scene3d: LibraryScene3dItem }>(res)
  return data.scene3d
}

export async function renameScene3d(projectId: number, scene3dId: number, name: string): Promise<LibraryScene3dItem> {
  return updateScene3d(projectId, scene3dId, { name })
}

export async function updateScene3d(
  projectId: number,
  scene3dId: number,
  input: { name?: string; locationId?: number | null },
): Promise<LibraryScene3dItem> {
  const body: Record<string, unknown> = {}
  if (input.name !== undefined) body.name = input.name
  if (input.locationId !== undefined) body.location_id = input.locationId
  const res = await fetch(`/projects/${projectId}/workspace/library/scene3ds/${scene3dId}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const data = await handleJson<{ ok: boolean; scene3d: LibraryScene3dItem }>(res)
  return data.scene3d
}

export async function deleteScene3d(projectId: number, scene3dId: number): Promise<void> {
  const res = await fetch(`/projects/${projectId}/workspace/library/scene3ds/${scene3dId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: mutationHeaders(),
  })
  await handleJson<{ ok: boolean }>(res)
}

export type LibraryScene3dViewpointItem = {
  id: number
  scene3d_id: number
  name: string | null
  camera_state: unknown
  media_type: "image"
  url: string | null
  thumb_url: string | null
  download_url: string | null
  created_at: string
}

export async function createScene3dViewpoint(
  projectId: number,
  scene3dId: number,
  input: { file: Blob; name?: string; cameraState: unknown; filename?: string },
): Promise<LibraryScene3dViewpointItem> {
  const form = new FormData()
  form.append("file", input.file, input.filename ?? "viewpoint.jpg")
  if (input.name) form.append("name", input.name)
  form.append("camera_state", JSON.stringify(input.cameraState ?? {}))
  const res = await fetch(`/projects/${projectId}/workspace/library/scene3ds/${scene3dId}/viewpoints`, {
    method: "POST",
    credentials: "same-origin",
    headers: mutationHeaders(),
    body: form,
  })
  const data = await handleJson<{ ok: boolean; viewpoint: LibraryScene3dViewpointItem }>(res)
  return data.viewpoint
}

export async function deleteScene3dViewpoint(projectId: number, scene3dId: number, viewpointId: number): Promise<void> {
  const res = await fetch(`/projects/${projectId}/workspace/library/scene3ds/${scene3dId}/viewpoints/${viewpointId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: mutationHeaders(),
  })
  await handleJson<{ ok: boolean }>(res)
}

/** Replace the scene tile cover. The previous poster blob is released on the server. */
export async function uploadScene3dPoster(
  projectId: number,
  scene3dId: number,
  blob: Blob,
  filename = "scene3d-poster.jpg",
): Promise<LibraryScene3dItem> {
  const form = new FormData()
  form.append("poster", blob, filename)
  const res = await fetch(`/projects/${projectId}/workspace/library/scene3ds/${scene3dId}/poster`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: mutationHeaders(),
    body: form,
  })
  const data = await handleJson<{ ok: boolean; scene3d: LibraryScene3dItem }>(res)
  return data.scene3d
}

/** Still cover from 3D Save (JPEG/PNG blob). */
export async function uploadPrevizPoster(
  projectId: number,
  previzId: number,
  blob: Blob,
  filename = "previz-poster.jpg",
): Promise<LibraryPrevizItem> {
  const form = new FormData()
  form.append("poster", blob, filename)
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/${previzId}/poster`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: mutationHeaders(),
    body: form,
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

/** Одна джоба считает серую и цветную карты. Повтор для той же версии — no-op. */
export async function generatePrevizDepth(projectId: number, previzId: number): Promise<LibraryPrevizItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/${previzId}/depth`, {
    method: "POST",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), Accept: "application/json" },
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

/** Re-record: replace source video + scene snapshot on the same previz. */
export async function replacePrevizMedia(
  projectId: number,
  previzId: number,
  input: {
    file: File
    durationMs?: number | null
    sceneState?: SceneStateSavePayload
  },
): Promise<LibraryPrevizItem> {
  const form = new FormData()
  form.append("file", input.file)
  if (input.durationMs != null) form.append("duration_ms", String(Math.round(input.durationMs)))
  if (input.sceneState != null) form.append("scene_state", JSON.stringify(input.sceneState))
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/${previzId}/media`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: mutationHeaders(),
    body: form,
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

export async function renamePreviz(projectId: number, previzId: number, name: string): Promise<LibraryPrevizItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/${previzId}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

/** Подрезка превиза: новый ролик на той же сцене. Исходный не меняется. */
export async function cutPreviz(
  projectId: number,
  previzId: number,
  range: { sourceInMs: number; sourceOutMs: number },
): Promise<LibraryPrevizItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/${previzId}/cut`, {
    method: "POST",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      source_in_ms: range.sourceInMs,
      source_out_ms: range.sourceOutMs,
    }),
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

/** Привязка/отвязка превиза к шоту (shotId: null — detach), как link_shot у аниматика. */
export async function linkPrevizToShot(
  projectId: number,
  previzId: number,
  shotId: number | null,
): Promise<LibraryPrevizItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/${previzId}/link_shot`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ shot_id: shotId }),
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

/** Превиз шота из видео библиотеки: сервер привязывает готовый превиз или заводит новый. */
export async function createPrevizFromSource(
  projectId: number,
  shotId: number,
  source: ShotFrameSource,
): Promise<LibraryPrevizItem> {
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/from_source`, {
    method: "POST",
    credentials: "same-origin",
    headers: { ...mutationHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ shot_id: shotId, source }),
  })
  const data = await handleJson<{ ok: boolean; previz: LibraryPrevizItem }>(res)
  return data.previz
}

export async function deletePreviz(projectId: number, previzId: number): Promise<void> {
  const res = await fetch(`/projects/${projectId}/workspace/library/previzes/${previzId}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: mutationHeaders(),
  })
  await handleJson<{ ok: boolean }>(res)
}
